import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLabelsAdmin, useCreateLabel, useUpdateLabel, useDeleteLabel } from '../../hooks/useProjects';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { Modal, ConfirmModal } from '../../components/common/Modal';
import type { AdminLabel } from '../../services/api/projects';
import { labelChipStyle } from '../../utils/labelColor';

interface LabelFormData {
  name: string;
  color: string;
  description: string;
  isSystem: boolean;
}

const emptyForm: LabelFormData = { name: '', color: '#3b82f6', description: '', isSystem: false };

function LabelFormModal({
  label,
  onClose,
}: {
  label: AdminLabel | null; // null = create mode
  onClose: () => void;
}) {
  const createMutation = useCreateLabel();
  const updateMutation = useUpdateLabel();
  const isEditing = label !== null;

  const [form, setForm] = useState<LabelFormData>(emptyForm);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (label) {
      setForm({
        name: label.name || '',
        color: label.color || '#3b82f6',
        description: label.description || '',
        isSystem: label.isSystem,
      });
    }
  }, [label]);

  const updateField = (field: keyof LabelFormData, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    try {
      if (isEditing) {
        await updateMutation.mutateAsync({
          id: label.id,
          input: {
            name: form.name.trim(),
            color: form.color || null,
            description: form.description.trim() || null,
            isSystem: form.isSystem,
          },
        });
      } else {
        await createMutation.mutateAsync({
          name: form.name.trim(),
          color: form.color || undefined,
          description: form.description.trim() || undefined,
          isSystem: form.isSystem,
        });
      }
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Failed to save label.');
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const inputClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

  return (
    <Modal isOpen onClose={onClose} title={isEditing ? 'Edit Label' : 'New Label'} size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => updateField('name', e.target.value)}
            className={inputClass}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={form.color}
              onChange={(e) => updateField('color', e.target.value)}
              className="h-10 w-14 rounded border border-gray-300 cursor-pointer"
            />
            <input
              type="text"
              value={form.color}
              onChange={(e) => updateField('color', e.target.value)}
              className={inputClass}
              placeholder="#3b82f6"
            />
            {form.name && (
              <span
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
                style={{
                  ...labelChipStyle(form.color),
                }}
              >
                {form.name}
              </span>
            )}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <input
            type="text"
            value={form.description}
            onChange={(e) => updateField('description', e.target.value)}
            className={inputClass}
            placeholder="Optional description"
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="labelIsSystem"
            checked={form.isSystem}
            onChange={(e) => updateField('isSystem', e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="labelIsSystem" className="text-sm text-gray-700">System label</label>
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
          <Button type="submit" isLoading={isPending}>{isEditing ? 'Save Changes' : 'Create'}</Button>
        </div>
      </form>
    </Modal>
  );
}

export default function LabelManagementPage() {
  const navigate = useNavigate();
  const { data: labels, isLoading } = useLabelsAdmin();
  const deleteMutation = useDeleteLabel();

  const [editingLabel, setEditingLabel] = useState<AdminLabel | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const handleDelete = async () => {
    if (deleteId === null) return;
    await deleteMutation.mutateAsync(deleteId);
    setDeleteId(null);
  };

  if (isLoading) {
    return <div className="flex justify-center py-8"><Spinner /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <button onClick={() => navigate('/admin')} className="text-sm text-gray-500 hover:text-gray-700 mb-1">
          &larr; Back to Settings
        </button>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900">Labels</h1>
          <Button size="sm" onClick={() => setShowCreateModal(true)}>New</Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Color</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Is System</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">#Projects</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {labels?.map((label) => (
              <tr key={label.id} className="hover:bg-gray-50">
                <td className="px-6 py-3 text-sm font-medium text-gray-900">{label.name || '-'}</td>
                <td className="px-6 py-3">
                  {label.color ? (
                    <span
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                      style={labelChipStyle(label.color)}
                    >
                      {label.name}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">-</span>
                  )}
                </td>
                <td className="px-6 py-3 text-sm text-gray-600">{label.description || ''}</td>
                <td className="px-6 py-3 text-sm text-gray-600">{label.isSystem ? 'true' : 'false'}</td>
                <td className="px-6 py-3 text-sm text-gray-600 text-right">{label.projectCount}</td>
                <td className="px-6 py-3 text-right space-x-2">
                  <Button variant="ghost" size="sm" onClick={() => setEditingLabel(label)}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleteId(label.id)} className="text-red-600">Delete</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <LabelFormModal label={null} onClose={() => setShowCreateModal(false)} />
      )}

      {/* Edit Modal */}
      {editingLabel && (
        <LabelFormModal label={editingLabel} onClose={() => setEditingLabel(null)} />
      )}

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Label"
        message="Are you sure you want to delete this label? It will be removed from all associated projects."
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
